import type { EventDropArg } from "@fullcalendar/core"
import dayjs from "dayjs"

import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { showNotification } from "../../../../utils/notification/show-notification"
import type { Event } from "../../types/types.ts"

interface EventDropUpdate {
  id: string
  type?: Event["type"]
  date: string
  start_at: string
  end_at: string
  all_day: boolean
}

// FullCalendar runs a named zone without a timezone plugin, so event dates
// arrive as wall time encoded as UTC, and the normalization below stays in
// that frame.
export const buildEventDropUpdate = (
  event: Pick<EventDropArg["event"], "start" | "end" | "allDay"> & {
    extendedProps: Record<string, any>
  },
  timezone?: string
): EventDropUpdate | null => {
  const { extendedProps } = event

  const start = dayjs(event.start).utc()
  const end = dayjs(event.end).utc()

  // Only timed meetings reschedule by drag. The week view's all-day row is a
  // valid drop target that strips the end, so a payload built from it would
  // flip the meeting to all-day off an invalid date.
  if (event.allDay || !start.isValid() || !end.isValid()) return null

  // Resolving each edge into the zone on its own moves them by different
  // offsets across a DST boundary, silently resizing the meeting.
  // An undefined zone makes dayjs.tz() read the format string as the zone.
  const startAt = dayjs.tz(
    start.format("YYYY-MM-DD HH:mm"),
    "YYYY-MM-DD HH:mm",
    timezone || dayjs.tz.guess()
  )
  const endAt = startAt.add(end.diff(start, "minute"), "minute")

  return {
    id: extendedProps?.id,
    type: extendedProps?.type?.toUpperCase(),
    date: start.format(DATE_FORMAT),
    start_at: startAt.format(),
    end_at: endAt.format(),
    all_day: false
  }
}

export const rescheduleDroppedEvent = async (
  { event, revert }: Pick<EventDropArg, "event" | "revert">,
  timezone: string | undefined,
  update: (data: EventDropUpdate) => Promise<unknown>
): Promise<void> => {
  const payload = buildEventDropUpdate(event, timezone)

  if (!payload) {
    revert()
    showNotification.warning({
      message: "Meeting not moved",
      description: event.allDay
        ? "A meeting cannot be moved into the all-day row."
        : "That drop did not resolve to a valid time range."
    })
    return
  }

  try {
    await update(payload)
  } catch (error) {
    console.error("Failed to update event:", error)
    revert()
  }
}

export type { EventDropUpdate }
